import {FC, useRef, useState} from 'react';
//mui
import {Box, IconButton} from '@mui/material';
//helpers
import {getErrorMessage, requestAppleAuthorization} from '@/utils/helpers';
import {IAuthAppleRequest} from '@/utils/types';
//styles
import {allSocialItemsStyle} from './AllSocialItems.styles';
import {CredentialResponse, GoogleLogin} from '@react-oauth/google';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {authApple, authGoogle} from '@/store/organization';
import {authAppleUser, authGoogleUser, getUserInfo} from '@/store/user';
import {ROUTER_URL, USER_TYPE} from '@/utils/enums';
import {orgStorage, UserService, userStorage} from '@/utils/services';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {ReactComponent as AppleIcon} from '@/assets/icons/apple.svg';
import {getCurrentEventSelector} from '@/store/organization-event';

interface AllSocialItemsProps {
  userType?: USER_TYPE;
  onSocialLoginSuccess?: () => void;
  onGoogleSignupSuccess?: (idToken: string) => void;
  onAppleSignupSuccess?: (request: IAuthAppleRequest) => Promise<void>;
}

const AllSocialItems: FC<AllSocialItemsProps> = ({
  userType = USER_TYPE.ORGANIZATION,
  onSocialLoginSuccess,
  onGoogleSignupSuccess,
  onAppleSignupSuccess,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {slug} = useParams();

  const event = useAppSelector(getCurrentEventSelector);

  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const appleRequestInFlight = useRef(false);

  const getAuthErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    return getErrorMessage(error as Parameters<typeof getErrorMessage>[0]);
  };

  const handleSocialLoginSuccess = () => {
    if (userType === USER_TYPE.USER) {
      userStorage.set('userType', USER_TYPE.USER);
      onSocialLoginSuccess?.();

      const returnTo = sessionStorage.getItem('private_return_to');
      sessionStorage.removeItem('private_return_to');
      navigate(returnTo ?? `/${slug}`, {replace: true});
      return;
    }

    orgStorage.set('userType', USER_TYPE.ORGANIZATION);
    navigate(ROUTER_URL.ORGANIZATION);
  };

  const handleGoogleSuccess = async (tokenResponse: CredentialResponse) => {
    if (!tokenResponse.credential) {
      toast.error('Google authorization failed');
      return;
    }

    try {
      if (onGoogleSignupSuccess && userType === USER_TYPE.ORGANIZATION) {
        onGoogleSignupSuccess(tokenResponse.credential);
        return;
      }

      if (userType === USER_TYPE.USER && event?.id) {
        const {userInfo} = await dispatch(authGoogleUser({idToken: tokenResponse.credential})).unwrap();
        if (userInfo && userInfo.id) {
          const {data} = await UserService.ensureUserHaveAttendeeIdentity(event?.id);

          if (data.attendeeId) {
            await dispatch(getUserInfo(userInfo.id)).unwrap();
          }
        }
      } else {
        await dispatch(authGoogle({idToken: tokenResponse.credential})).unwrap();
      }

      handleSocialLoginSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAppleClick = async () => {
    if (appleRequestInFlight.current) return;
    appleRequestInFlight.current = true;
    setIsAppleLoading(true);

    try {
      const appleRequest = await requestAppleAuthorization();

      if (onAppleSignupSuccess && userType === USER_TYPE.ORGANIZATION) {
        await onAppleSignupSuccess(appleRequest);
        return;
      }

      if (userType === USER_TYPE.USER && event?.id) {
        const {userInfo} = await dispatch(authAppleUser(appleRequest)).unwrap();
        if (userInfo && userInfo.id) {
          const {data} = await UserService.ensureUserHaveAttendeeIdentity(event?.id);

          if (data.attendeeId) {
            await dispatch(getUserInfo(userInfo.id)).unwrap();
          }
        }
      } else {
        await dispatch(authApple(appleRequest)).unwrap();
      }

      handleSocialLoginSuccess();
    } catch (error) {
      toast.error(getAuthErrorMessage(error));
    } finally {
      appleRequestInFlight.current = false;
      setIsAppleLoading(false);
    }
  };

  return (
    <Box sx={allSocialItemsStyle.wrapper}>
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={() => toast.error('Google authorization failed')}
        type="icon"
        theme="outline"
        size="large"
        shape="square"
        width="40"
        logo_alignment="center"
      />

      <IconButton
        disableRipple
        disabled={isAppleLoading}
        // aria-label={`Continue with ${title}`}
        onClick={handleAppleClick}
        sx={allSocialItemsStyle.icon}
      >
        <AppleIcon />
      </IconButton>
    </Box>
  );
};

export default AllSocialItems;
